import React, { useState, useEffect, useMemo } from 'react';
import { useUpdateProfile, useJobRoles } from '../../hooks/useUserQueries';
import AddressInput from '../common/AddressInput';
import './ProfileForm.css';

const ProfileForm = ({ user }) => {
  const updateProfileMutation = useUpdateProfile();
  
  // Fetch job roles from backend
  const { data: jobRolesData, isLoading: isLoadingJobRoles } = useJobRoles();
  
  // Filter and sort active job roles
  const jobRoles = useMemo(() => {
    if (!jobRolesData) return [];
    return jobRolesData
      .filter(role => role.is_active)
      .sort((a, b) => a.job_role_name.localeCompare(b.job_role_name));
  }, [jobRolesData]);
  
  // Helper function to convert travel_miles integer to range format
  const convertMilesToRange = (miles) => {
    if (!miles && miles !== 0) return '';
    if (miles >= 100) return '100+';
    if (miles >= 76) return '76-100';
    if (miles >= 51) return '51-75';
    if (miles >= 26) return '26-50';
    if (miles >= 11) return '11-25';
    if (miles >= 0) return '0-10';
    return '';
  };

  // Get job_role_key from user data (from job_role_detail or job_role)
  const getUserJobRoleKey = useMemo(() => {
    if (user?.job_role_detail?.job_role_key) {
      return user.job_role_detail.job_role_key;
    }
    // Fallback: if we have job_role ID, find the matching job role
    if (user?.job_role && jobRoles.length > 0) {
      const foundRole = jobRoles.find(role => role.id === user.job_role);
      return foundRole?.job_role_key || '';
    }
    return '';
  }, [user, jobRoles]);

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    contact_no: user?.contact_no || '',
    address: user?.address || '',
    latitude: user?.latitude || null,
    longitude: user?.longitude || null,
    dob: user?.dob || '',
    job_role: getUserJobRoleKey,
    travel_miles: convertMilesToRange(user?.travel_miles),
    gdc_number: user?.gdc_number || '',
    practice_name: user?.practice_name || '',
    registered_number: user?.registered_number || '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        contact_no: user.contact_no || '',
        address: user.address || '',
        latitude: user.latitude || null,
        longitude: user.longitude || null,
        dob: user.dob || '',
        job_role: getUserJobRoleKey,
        travel_miles: convertMilesToRange(user.travel_miles),
        gdc_number: user.gdc_number || '',
        practice_name: user.practice_name || '',
        registered_number: user.registered_number || '',
      });
    }
  }, [user, getUserJobRoleKey]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For GDC number, only allow digits
    let processedValue = value;
    if (name === 'gdc_number') {
      // Remove any non-digit characters
      processedValue = value.replace(/\D/g, '');
      // Limit to 6 digits maximum
      if (processedValue.length > 6) {
        processedValue = processedValue.slice(0, 6);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLocationSelect = (locationData) => {
    setFormData(prev => ({
      ...prev,
      address: locationData.address,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
    }));
    
    // Clear address error if present
    if (errors.address) {
      setErrors(prev => ({
        ...prev,
        address: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.contact_no.trim()) {
      newErrors.contact_no = 'Phone number is required';
    } else if (!formData.contact_no.startsWith('+')) {
      newErrors.contact_no = 'Phone number must start with + (e.g., +1234567890)';
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }

    // Employee-specific validations
    if (user?.role_name === 'employee') {
      if (!formData.job_role || !formData.job_role.trim()) {
        newErrors.job_role = 'Job role is required';
      } else if (jobRoles.length > 0) {
        const selectedRole = jobRoles.find(role => role.job_role_key === formData.job_role);
        if (!selectedRole) {
          newErrors.job_role = 'Please select a valid job role';
        }
      }
      if (!formData.travel_miles) {
        newErrors.travel_miles = 'Please select your travel distance';
      }
      if (!formData.gdc_number.trim()) {
        newErrors.gdc_number = 'GDC number is required';
      } else if (!/^\d{5,6}$/.test(formData.gdc_number.trim())) {
        newErrors.gdc_number = 'GDC number must be 5 or 6 digits';
      }
    }

    // Employer-specific validations
    if (user?.role_name === 'employer') {
      if (!formData.practice_name.trim()) {
        newErrors.practice_name = 'Practice/Clinic name is required';
      }
      if (!formData.registered_number.trim()) {
        newErrors.registered_number = 'Company registration number is required';
      }
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        // Convert travel distance to integer for employees (same as signup)
        const submitData = { ...formData };
        
        // Remove email from submit data (email is disabled/non-editable)
        delete submitData.email;
        
        if (submitData.travel_miles) {
          // Extract number from range (e.g., "0-10" -> 10, "100+" -> 100)
          const milesValue = submitData.travel_miles.includes('+') 
            ? parseInt(submitData.travel_miles.replace('+', ''))
            : parseInt(submitData.travel_miles.split('-')[1] || submitData.travel_miles);
          submitData.travel_miles = milesValue;
        }
        
        // Convert job_role_key to job_role ID for backend
        // Backend expects job_role (ID), not job_role_key
        if (submitData.job_role && jobRoles.length > 0) {
          const selectedRole = jobRoles.find(role => role.job_role_key === submitData.job_role);
          if (selectedRole) {
            submitData.job_role = selectedRole.id;
          } else {
            throw new Error('Invalid job role selected');
          }
        }
        
        // Ensure latitude and longitude are properly formatted as numbers
        // They're already in submitData from formData spread, just ensure they're numbers
        if (submitData.latitude != null) {
          submitData.latitude = parseFloat(submitData.latitude);
        }
        if (submitData.longitude != null) {
          submitData.longitude = parseFloat(submitData.longitude);
        }
        
        await updateProfileMutation.mutateAsync(submitData);
      } catch (error) {
        // Error toast is handled by the hook
        console.error('Profile update error:', error);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="profile-form">
      <div className="profile-form__section">
        <h2 className="profile-form__section-title">Personal Information</h2>
        
        <div className="profile-form__row">
          <div className="profile-form__field">
            <label htmlFor="first_name" className="profile-form__label">
              First Name
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`profile-form__input ${errors.first_name ? 'profile-form__input--error' : ''}`}
              required
            />
            {errors.first_name && (
              <span className="profile-form__error">{errors.first_name}</span>
            )}
          </div>

          <div className="profile-form__field">
            <label htmlFor="last_name" className="profile-form__label">
              Last Name
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={`profile-form__input ${errors.last_name ? 'profile-form__input--error' : ''}`}
              required
            />
            {errors.last_name && (
              <span className="profile-form__error">{errors.last_name}</span>
            )}
          </div>
        </div>

        <div className="profile-form__row">
          <div className="profile-form__field">
            <label htmlFor="email" className="profile-form__label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`profile-form__input ${errors.email ? 'profile-form__input--error' : ''}`}
              disabled
              required
            />
            <p className="profile-form__hint">Email cannot be changed. Please contact support if you need to update your email.</p>
            {errors.email && (
              <span className="profile-form__error">{errors.email}</span>
            )}
          </div>

          <div className="profile-form__field">
            <label htmlFor="contact_no" className="profile-form__label">
              Phone Number
            </label>
            <input
              type="tel"
              id="contact_no"
              name="contact_no"
              value={formData.contact_no}
              onChange={handleChange}
              className={`profile-form__input ${errors.contact_no ? 'profile-form__input--error' : ''}`}
              placeholder="+1234567890"
              required
            />
            {errors.contact_no && (
              <span className="profile-form__error">{errors.contact_no}</span>
            )}
          </div>
        </div>

        <div className="profile-form__field">
          <label htmlFor="dob" className="profile-form__label">
            Date of Birth
          </label>
          <input
            type="date"
            id="dob"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            className={`profile-form__input ${errors.dob ? 'profile-form__input--error' : ''}`}
            max={new Date().toISOString().split('T')[0]}
            required
          />
          {errors.dob && (
            <span className="profile-form__error">{errors.dob}</span>
          )}
        </div>
      </div>

      {user?.role_name === 'employee' && (
        <div className="profile-form__section">
          <h2 className="profile-form__section-title">Professional Information</h2>
          
          <div className="profile-form__row">
            <div className="profile-form__field">
              <label htmlFor="job_role" className="profile-form__label">
                Job Role
              </label>
              <select
                id="job_role"
                name="job_role"
                value={formData.job_role}
                onChange={handleChange}
                disabled
                className={`profile-form__select ${errors.job_role ? 'profile-form__select--error' : ''}`}
                required
              >
                <option value="">
                  {isLoadingJobRoles ? 'Loading job roles...' : 'Select your job role'}
                </option>
                {jobRoles.map(role => (
                  <option key={role.id} value={role.job_role_key}>
                    {role.job_role_name}
                  </option>
                ))}
              </select>
              {errors.job_role && (
                <span className="profile-form__error">{errors.job_role}</span>
              )}
            </div>

            <div className="profile-form__field">
              <label htmlFor="travel_miles" className="profile-form__label">
                Willing to Travel (Miles)
              </label>
              <select
                id="travel_miles"
                name="travel_miles"
                value={formData.travel_miles}
                onChange={handleChange}
                className={`profile-form__select ${errors.travel_miles ? 'profile-form__select--error' : ''}`}
                required
              >
                <option value="">Select travel distance</option>
                <option value="0-10">0-10 miles</option>
                <option value="11-25">11-25 miles</option>
                <option value="26-50">26-50 miles</option>
                <option value="51-75">51-75 miles</option>
                <option value="76-100">76-100 miles</option>
                <option value="100+">100+ miles</option>
              </select>
              {errors.travel_miles && (
                <span className="profile-form__error">{errors.travel_miles}</span>
              )}
            </div>
          </div>

          <div className="profile-form__field">
            <label htmlFor="gdc_number" className="profile-form__label">
              GDC Number
            </label>
            <input
              type="text"
              id="gdc_number"
              name="gdc_number"
              value={formData.gdc_number}
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]{5,6}"
              maxLength={6}
              className={`profile-form__input ${errors.gdc_number ? 'profile-form__input--error' : ''}`}
              placeholder="Enter your GDC registration number (5-6 digits)"
              disabled
              required
            />
            {errors.gdc_number && (
              <span className="profile-form__error">{errors.gdc_number}</span>
            )}
          </div>
        </div>
      )}

      {user?.role_name === 'employer' && (
        <div className="profile-form__section">
          <h2 className="profile-form__section-title">Company Information</h2>
          
          <div className="profile-form__row">
            <div className="profile-form__field">
              <label htmlFor="practice_name" className="profile-form__label">
                Practice/Clinic Name
              </label>
              <input
                type="text"
                id="practice_name"
                name="practice_name"
                value={formData.practice_name}
                onChange={handleChange}
                className={`profile-form__input ${errors.practice_name ? 'profile-form__input--error' : ''}`}
                placeholder="Enter your practice or clinic name"
                required
              />
              {errors.practice_name && (
                <span className="profile-form__error">{errors.practice_name}</span>
              )}
            </div>

            <div className="profile-form__field">
              <label htmlFor="registered_number" className="profile-form__label">
                Company Registration Number
              </label>
              <input
                type="text"
                id="registered_number"
                name="registered_number"
                value={formData.registered_number}
                onChange={handleChange}
                className={`profile-form__input ${errors.registered_number ? 'profile-form__input--error' : ''}`}
                placeholder="Enter your company registration number"
                required
              />
              {errors.registered_number && (
                <span className="profile-form__error">{errors.registered_number}</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="profile-form__section">
        <h2 className="profile-form__section-title">Address</h2>
        
        <div className="profile-form__field">
          <label htmlFor="address" className="profile-form__label">
            Address
          </label>
          <AddressInput
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            onLocationSelect={handleLocationSelect}
            placeholder="Enter your full address"
            error={errors.address}
            required
            className={errors.address ? 'address-input--error' : ''}
          />
        </div>
      </div>


      <div className="profile-form__actions">
        <button
          type="submit"
          className="profile-form__save-button"
          disabled={updateProfileMutation.isPending}
        >
          {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;







